import CoreAnalytics from './coreAnalytics';

/** @typedef {import('./analyticsTypes').SessionActivityEventPayload} */
/** @typedef {import('./analyticsTypes').SessionActivityWithAppointmentEventPayload} */
/** @typedef {import('./analyticsTypes').SessionActivityReopenedEventPayload} */

/**
 * Analytics service related to session functionality.
 */
class SessionAnalytics extends CoreAnalytics {
  EVENT_NAMES = {
    ACTIVITY_REOPENED: 'session_activity_reopened',
    ACTIVITY_NOTES_EDITED_AFTER_REOPENED: 'session_activity_notes_edited_after_reopened',
    ACTIVITY_COMPLETED: 'session_activity_completed',
    ACTIVITY_LINKED_TO_APPOINTMENT_MANUALLY: 'session_activity_linked_to_appointment_manually',
    STAFF_ACTIVITY_CREATED_FROM_PAST_APPOINTMENT: 'staff_session_activity_created_from_past_appointment',
  };

  /**
   * Track when a session activity is reopened.
   * @param {SessionActivityReopenedEventPayload} sessionActivityEventPayload
   */
  trackActivityReopened(sessionActivityEventPayload) {
    if (!sessionActivityEventPayload.sessionActivityId) {
      return;
    }

    this.track(this.EVENT_NAMES.ACTIVITY_REOPENED, {
      ...sessionActivityEventPayload,
      eventVersion: 3,
    });
  }

  /**
   * Track when a session activity is completed.
   *
   * @param {SessionActivityEventPayload} sessionActivityEventPayload
   */
  trackActivityCompleted(sessionActivityEventPayload) {
    if (!sessionActivityEventPayload?.sessionActivityId) {
      return;
    }

    this.track(this.EVENT_NAMES.ACTIVITY_COMPLETED, {
      ...sessionActivityEventPayload,
      eventVersion: 1,
    });
  }

  /**
   * Track when session activity notes are updated after the activity was reopened.
   *
   * @param {SessionActivityEventPayload} sessionActivityEventPayload
   */
  trackActivityNotesEditedAfterReopened(sessionActivityEventPayload) {
    if (!sessionActivityEventPayload?.sessionActivityId) {
      return;
    }

    this.track(this.EVENT_NAMES.ACTIVITY_NOTES_EDITED_AFTER_REOPENED, {
      ...sessionActivityEventPayload,
      eventVersion: 1,
    });
  }

  /**
   * Track when session activity notes are updated after the activity was reopened.
   *
   * @param {SessionActivityWithAppointmentEventPayload} sessionActivityEventPayload
   */
  trackActivityLinkedToAppointmentManually(sessionActivityEventPayload) {
    if (!sessionActivityEventPayload?.sessionActivityId || !sessionActivityEventPayload.appointmentId) {
      return;
    }

    this.track(this.EVENT_NAMES.ACTIVITY_LINKED_TO_APPOINTMENT_MANUALLY, {
      ...sessionActivityEventPayload,
      eventVersion: 1,
    });
  }

  /**
   * Track when session activity is created from a past appointment.
   *
   * @param {SessionActivityWithAppointmentEventPayload} sessionActivityEventPayload
   */
  trackActivityCreatedFromPastAppointment(sessionActivityEventPayload) {
    if (!sessionActivityEventPayload?.sessionActivityId || !sessionActivityEventPayload.appointmentId) {
      return;
    }

    this.track(this.EVENT_NAMES.STAFF_ACTIVITY_CREATED_FROM_PAST_APPOINTMENT, {
      ...sessionActivityEventPayload,
      eventVersion: 1,
    });
  }
}

export default new SessionAnalytics();
